/**
 * 20261005 - Canvas Coverage overview feature module: the inbound verb of the `?page=canvas-coverage`
 * route (Unity's ScreenplayWebViewController on its coverage page), spec docs/superpowers/specs/
 * 2026-10-05-canvas-coverage-overview-design.md §12.2.
 *
 *   coverageShotLens  {shotId, mm, coverageEditSeq}   the whole mm that shot's canvas tile now shows,
 *                                                     and the highest applyCoverageShotEdit seq Unity
 *                                                     had processed when it said so
 *
 * Unity sends it after every accepted lens write (the app's lens range may have moved the number the
 * user typed), and whenever the lens of the shot OPEN in the canvas changes some other way while the
 * page is up (the 3D aperture handle, the Viewfinder, undo / redo). Whether the row takes it is the
 * page's call (canvasCoverageSync.canAdopt): this module checks the shape and hands it over.
 *
 * A registration rather than an import, exactly like timeline.ts's coverageEditSeq hand-off: the page
 * is a lazy route, and nothing of it may join the bridge's eager graph. No listener (any other route)
 * means the report is dropped — it is a table verb everywhere so it never reads as unclaimed.
 */

import type { HandlerMap } from '../dispatch';
import { diagLog } from '../transport';

// ─── Message types ───────────────────────────────────────────────────────

export interface CoverageShotLensMessage {
  type: 'coverageShotLens';
  payload: { shotId: number; mm: number; coverageEditSeq: number };
}

export type CanvasCoverageMessage = CoverageShotLensMessage;

export type CoverageShotLens = CoverageShotLensMessage['payload'];

// ─── Payload guard ───────────────────────────────────────────────────────

/** All three fields or nothing: a shot to put it on, a lens (0 is "no camera", not a lens), and the
 *  seq that says which of the page's writes it already reflects — without that the page cannot tell
 *  it from a report that predates the user's own edit. */
export function readCoverageShotLens(raw: unknown): CoverageShotLens | null {
  const p = raw as Partial<Record<keyof CoverageShotLens, unknown>> | null | undefined;
  if (p === null || p === undefined || typeof p !== 'object') return null;
  const { shotId, mm, coverageEditSeq } = p;
  if (typeof shotId !== 'number' || !Number.isFinite(shotId) || shotId <= 0) return null;
  if (typeof mm !== 'number' || !Number.isFinite(mm) || mm <= 0) return null;
  if (typeof coverageEditSeq !== 'number' || !Number.isFinite(coverageEditSeq) || coverageEditSeq < 0) return null;
  return { shotId, mm, coverageEditSeq };
}

// ─── Page hand-off ───────────────────────────────────────────────────────

let coverageShotLensListener: ((lens: CoverageShotLens) => void) | null = null;

/** Install the listener; the returned disposer clears it only while it is still this one. */
export function registerCoverageShotLensListener(fn: (lens: CoverageShotLens) => void): () => void {
  coverageShotLensListener = fn;
  return () => { if (coverageShotLensListener === fn) coverageShotLensListener = null; };
}

// ─── Inbound handler ─────────────────────────────────────────────────────

export const canvasCoverageHandlers = {
  coverageShotLens: (msg: CoverageShotLensMessage) => {
    const lens = readCoverageShotLens(msg.payload);
    if (lens === null) {
      diagLog('CanvasCoverage', `coverageShotLens dropped: malformed payload ${JSON.stringify(msg.payload)}`);
      return;
    }
    coverageShotLensListener?.(lens);
  },
} satisfies HandlerMap;
